import React from "react";

const MindsetBlockersVideo = ({ subsection, saveProgress }) => {
  const about = (
    <div className="space-y-5 pt-2 pb-6 text-body leading-7 text-[#484848]">
      <p>
        This lesson explains why people understand leverage but still avoid using it. It maps seven beliefs that block delegation, tooling, and asking for help, like worth equals effort, only I can do it right, teaching is slow, being indispensable is safe, do not burden others, if they shine I fade, and busyness equals importance. The thread underneath is fear, and the shift is to value outcomes at scale, systems, and making yourself replaceable.
      </p>
    </div>
  );

const transcript = (
  <div className="pt-2 pb-6 space-y-4 text-body leading-7 text-[#323232] overflow-y-scroll font-gotham">
    <p>
      Now you know leverage is important. You understand the concept. But when
      the moment comes, when you could ask for more people, when you could
      delegate that project, when you could build a tool instead of doing it
      manually for the hundredth time....you don't.
    </p>

    <p>Something stops you.</p>

    <p>
      It's not because resources are unavailable. You keep telling yourself
      that it has to be you doing the work, that the grind is inevitable.
    </p>

    <p>Let’s explore seven beliefs that block people from seeking leverage.</p>

    <p>
      <strong>MINDSET 1: Worth = Effort</strong>
    </p>

    <p>
      This is when you believe if it isn't hard, it isn't valuable, that your
      worth is measured by how much you struggle.
    </p>

    <p>
      So when something becomes easy, through automation, through delegation,
      through a better process, it feels less valuable. Like you're cheating
      somehow.
    </p>

    <p>
      Instead rethink, worth equals outcomes at scale. Hard work is just raw
      material. Leverage is what creates value. A carpenter isn't less valuable
      because they use a saw instead of their teeth.
    </p>

    <p>
      <strong>MINDSET 2: Only I Can Do It Right</strong>
    </p>

    <p>
      If you hand it off, it won't meet your standards right? If you keep
      thinking control equals quality it keeps you a doer forever. You become
      the bottleneck.
    </p>

    <p>
      It's not control <em>but process</em> that equals quality. Standards and
      checks beat heroics. Document your standards, build in review points, and
      trust the system.
    </p>

    <p>
      <strong>MINDSET 3: Teaching Slows Things Down</strong>
    </p>

    <p>
      We’ve all been there. You keep thinking “I don't have time to teach this.
      It's faster if I just do it myself."
    </p>

    <p>
      And you're right, <em>this week</em> it's faster to do it yourself. But
      you're not optimizing for this week. You're optimizing for your career.
    </p>

    <p>
      One lesson equals many saves. Train them once, reuse forever. Every minute
      spent teaching is an investment that pays back 10x.
    </p>

    <p>
      Say, a task takes you 1 minute. Teaching someone else takes 10. You feel
      like you wasted 9 minutes, right?
    </p>

    <p>
      But if that task repeats for the 52 weeks in a year, you’ve just saved 52
      minutes minus those 9 you spent teaching. That’s 43 minutes back in your
      pocket, every year, per task.
    </p>

    <p>
      Now imagine doing that across your whole team. That’s how leaders multiply
      time instead of managing it.
    </p>

    <p>
      <strong>MINDSET 4: Being Indispensable Is Safe</strong>
    </p>

    <p>
      If you're the only one who knows how to do something, you're unfireable.
      Your instinct is to hoard the context and protect your territory.
    </p>

    <p>
      Reality check: that means you're too valuable to promote. If your team
      can't function without you doing the work, how can they move you up?
    </p>

    <p>
      Being replaceable is promotable. Teams that run without you prove you're
      ready for more.
    </p>

    <p>
      <strong>MINDSET 5: Don't Burden Others</strong>
    </p>

    <p>
      It's unfair to ask others to take on your work. That makes you a bad
      person or a bad teammate.
    </p>

    <p>
      But what you're actually doing is robbing them of growth opportunities.
    </p>

    <p>
      Delegation creates opportunities and skills for others. You're not
      burdening them,you're giving them a chance to shine.
    </p>

    <p>
      <strong>MINDSET 6: If They Shine, I Fade</strong>
    </p>

    <p>
      There's only so much recognition to go around. If your team member gets
      praised, that's praise that could've been yours.
    </p>

    <p>This is zero-sum thinking. And it's dead wrong.</p>

    <p>
      Their wins are your scope multiplier. Great leaders are judged by the
      teams they build, not the tasks they hoard.
    </p>

    <p>
      <strong>MINDSET 7: Busyness = Importance</strong>
    </p>

    <p>
      A packed calendar proves your value. If you're not busy, you're not
      contributing.
    </p>

    <p>
      But leadership isn't about being maxed out. It's about having the space
      to think strategically, to build relationships, to solve problems{" "}
      <em>before</em> they become fires.
    </p>

    <p>
      Empty blocks signal level-up. Space in your calendar means you've built
      enough leverage to focus on what matters.
    </p>

    <p>Notice the pattern here?</p>

    <p>
      Every one of these beliefs is rooted in fear. Fear of being irrelevant.
      Fear of losing control. Fear of being exposed. Fear of not being
      "enough."
    </p>

    <p>
      And those fears made sense early in your career. When you didn't have a
      track record, proving yourself through effort was the path. But now your
      job <em>is</em> to make sure the work gets done, by someone else.
    </p>
  </div>
);

  // const relatedLinks = (
  //   <div className="pt-2 pb-6">
  //     <ul className="list-disc list-inside text-body space-y-2">
  //       <li>
  //         <button type="button" className="text-link">
  //           Download Slides
  //         </button>
  //       </li>
  //       <li>
  //         <button type="button" className="text-link">
  //           Reference Reading: Career Transitions Report 2025
  //         </button>
  //       </li>
  //     </ul>
  //   </div>
  // );

  const notes = (
    <div className="pt-2 pb-6">
      <textarea
        rows={4}
        placeholder="Write your notes here..."
        className="w-full border border-gray-200 rounded-xl p-3 text-body focus:ring-2 focus:ring-brand/40 focus:outline-none"
        onBlur={(e) => saveProgress("in_progress", { notes: e.target.value })}
      />
    </div>
  );

  return { about, transcript, notes };
};

export default MindsetBlockersVideo;
